import UserScheduleValidation from "components/global/form/form-utils/UserScheduleValidation";
import { DAY_LABELS, DAY_ORDER, NEW_ORDER_RATE_LIMIT } from "./constants";
import { OrderRateLimit, WeeklySchedule } from "./types";

// Mirrors the list table's "Times" column in settings.html: one line per day that has a
// customer_input, "All Day" standing in for the literal 12AM-12AM default.
export const formatScheduleSummary = (activeSchedule: WeeklySchedule): string[] =>
  DAY_ORDER.filter((day) => activeSchedule[day]?.customer_input).map((day) => {
    const input = activeSchedule[day].customer_input;
    return `${DAY_LABELS[day]}: ${input === "12AM-12AM" ? "All Day" : input}`;
  });

const isSentinelStartDate = (value: string | null) => !value || new Date(value) < new Date("1900-01-01");
const isSentinelEndDate = (value: string | null) => !value || new Date(value) > new Date("2099-01-01");

// Mirrors addOrderRateLimitController's init(): editing an existing limit converts cents back to
// dollars for the subtotal fields, and infers whether the schedule/date-range step should start
// pre-expanded from whether any of that data is actually set (rather than trusting a flag, since
// the server doesn't persist one).
export const buildInitialOrderRateLimit = (
  existing: OrderRateLimit | null
): { orderRateLimit: OrderRateLimit; showActiveSchedule: boolean } => {
  if (!existing) {
    return { orderRateLimit: { ...NEW_ORDER_RATE_LIMIT }, showActiveSchedule: false };
  }

  const orderRateLimit = { ...existing };
  if (isSentinelStartDate(orderRateLimit.start_date)) orderRateLimit.start_date = null;
  if (isSentinelEndDate(orderRateLimit.end_date)) orderRateLimit.end_date = null;
  if (orderRateLimit.items_pretax_min) orderRateLimit.items_pretax_min /= 100;
  if (orderRateLimit.items_pretax_max) orderRateLimit.items_pretax_max /= 100;
  if (!orderRateLimit.prereq_tag_minutes_in_past) orderRateLimit.prereq_tag_minutes_in_past = 0;

  let showActiveSchedule = Boolean(orderRateLimit.start_date || orderRateLimit.end_date);
  DAY_ORDER.forEach((day) => {
    const times = orderRateLimit.active_schedule[day]?.military_times || [];
    if (times.length > 0 && (times[0][0] !== 0 || times[0][1] !== 2400)) showActiveSchedule = true;
  });

  return { orderRateLimit, showActiveSchedule };
};

export const validateSubtotalMinMax = (min: number | null, max: number | null): string | null => {
  if (!min && !max) return "Please provide a non-negative min, a non-negative max, or both.";
  if (min && min < 0) return "Subtotal minimum cannot be negative.";
  if (max && max < 0) return "Subtotal maximum cannot be negative.";
  if (min && max && min >= max) return "Max must be greater than min.";
  return null;
};

export const convertSubtotalToCents = (min: number | null, max: number | null) => ({
  items_pretax_min: min ? min * 100 : min,
  items_pretax_max: max ? max * 100 : max,
});

// Mirrors addOrderRateLimitController's validateOrderRateLimit(), field by field in the same
// order - including the min/max comparison's null-as-zero quirk (setting only one of the two
// doesn't trip the "min cannot be greater than max" check, matching legacy exactly).
export const validateOrderRateLimit = (orderRateLimit: OrderRateLimit): string | null => {
  if (!orderRateLimit.name_for_owner) {
    return "Name is required";
  }
  if (orderRateLimit.effect_type === "order_rate_limit" && !(orderRateLimit.max_orders_per_desired_time! > 0)) {
    return "Order Limit Per Time Slot is required";
  }
  if (orderRateLimit.effect_type === "tag_minmax" && !orderRateLimit.checkout_blocking_tag) {
    return "Tag to block checkout on is required";
  }
  if (
    orderRateLimit.effect_type === "tag_minmax" &&
    !(
      Number.isInteger(orderRateLimit.checkout_blocking_tag_min_qty) ||
      Number.isInteger(orderRateLimit.checkout_blocking_tag_max_qty)
    )
  ) {
    return "One of min and max must be specified";
  }
  if (
    orderRateLimit.effect_type === "tag_rate_limit" &&
    !(
      orderRateLimit.max_rate_limited_tag_per_desired_time! > 0 ||
      orderRateLimit.max_rate_limited_tag_per_day! > 0 ||
      orderRateLimit.max_rate_limited_tag_between_start_and_end! > 0
    )
  ) {
    return "One of tag rate limit choices must be greater than zero";
  }
  if (
    orderRateLimit.effect_type === "tag_rate_limit" &&
    orderRateLimit.max_rate_limited_tag_between_start_and_end! > 0 &&
    orderRateLimit.end_date === null
  ) {
    return "If Limit For Duration is greater than zero, End Date is required";
  }
  if (orderRateLimit.checkout_blocking_tag_min_qty! < 0 || orderRateLimit.checkout_blocking_tag_max_qty! < 0) {
    return "Min and max must be greater than zero";
  }
  if (orderRateLimit.checkout_blocking_tag_min_qty! > orderRateLimit.checkout_blocking_tag_max_qty!) {
    return "Min cannot be greater than max";
  }
  if (orderRateLimit.fulfillment_methods.length === 0) {
    return "Must select at least one Fulfillment Method";
  }
  if (orderRateLimit.location_ids.length === 0) {
    return "Must select at least one Location";
  }
  return null;
};

// Mirrors saveOrderRateLimit's schedule handling: a blank day means "always open" for that day,
// not "unenforced", so blanks are filled in before parsing.
export const buildValidatedSchedule = (
  activeSchedule: WeeklySchedule
): { isValid: boolean; schedule: WeeklySchedule } => {
  const filledSchedule: WeeklySchedule = {};
  DAY_ORDER.forEach((day) => {
    const customerInput = activeSchedule[day]?.customer_input || "";
    filledSchedule[day] = {
      customer_input: customerInput.length ? customerInput : "12AM-12AM",
      military_times: [],
    };
  });
  const result = UserScheduleValidation.parseUserInputtedSchedule(filledSchedule, true);
  return { isValid: result.userScheduleIsValid, schedule: result.scheduleAsMilitaryTime };
};

// Mirrors saveOrderRateLimit's "show_active_schedule === false" branch: no schedule restriction
// at all means every day is open 12AM-12AM and any date range is cleared.
export const buildAlwaysOpenSchedule = (): WeeklySchedule => {
  const schedule: WeeklySchedule = {};
  DAY_ORDER.forEach((day) => {
    schedule[day] = { customer_input: "12AM-12AM", military_times: [[0, 2400]] };
  });
  return schedule;
};

// Mirrors saveOrderRateLimit's final payload shaping: collapse an explicit "every fulfillment
// method" selection to the server's ['all'] sentinel, and clear unlocked_tag_max_qty when the
// "unlimited" option (represented as 0) was chosen.
export const buildSavePayload = (
  orderRateLimit: OrderRateLimit,
  schedule: WeeklySchedule,
  startDate: string | null,
  endDate: string | null,
  allFulfillmentMethodKeys: string[]
): OrderRateLimit => {
  const isAllFulfillmentMethods =
    allFulfillmentMethodKeys.length > 0 &&
    orderRateLimit.fulfillment_methods.length === allFulfillmentMethodKeys.length &&
    allFulfillmentMethodKeys.every((key) => orderRateLimit.fulfillment_methods.includes(key));

  return {
    ...orderRateLimit,
    active_schedule: schedule,
    start_date: startDate,
    end_date: endDate,
    fulfillment_methods: isAllFulfillmentMethods ? ["all"] : orderRateLimit.fulfillment_methods,
    unlocked_tag_max_qty: orderRateLimit.unlocked_tag_max_qty === 0 ? null : orderRateLimit.unlocked_tag_max_qty,
  };
};
